import React from 'react'
import styled, { css } from 'styled-components'

import { styles as S } from '@bob/design-system'

export type BrushTitleAnimatedProps = {
  animated?: boolean
  tag?: string
  Component?: React.ComponentType<
    {
      as?: string
      inView?: boolean
    } & React.HTMLAttributes<HTMLElement>
  >
  text: string
  inView?: boolean
  brushes?: {
    wordIndex: number
    comp: React.ComponentType<
      {
        inView?: boolean
      } & React.HTMLAttributes<HTMLElement>
    >
    props: {
      brushId: string
    }
  }[]
} & React.HTMLAttributes<HTMLElement>

export interface WordCSS extends React.CSSProperties {
  '--word-index': number
}

function BrushTitleAnimatedComponent({
  tag = 'h1',
  Component = S.heading.Title1,
  inView = false,
  text,
  brushes = [],
  ...rest
}: BrushTitleAnimatedProps) {
  const words = text.split(' ')

  return (
    <Component as={tag} inView={inView} {...rest}>
      {words.map((word, index) => {
        const brush = brushes.find((brush) => brush.wordIndex === index)
        const key = `${word}-${index}`

        return brush ? (
          <React.Fragment key={key}>
            <brush.comp {...brush.props} inView={inView} style={{ '--word-index': index } as WordCSS}>
              {word}
            </brush.comp>{' '}
          </React.Fragment>
        ) : (
          <React.Fragment key={key}>
            <span style={{ '--word-index': index } as WordCSS}>{word}</span>{' '}
          </React.Fragment>
        )
      })}
    </Component>
  )
}

export const BrushTitleAnimated = styled(BrushTitleAnimatedComponent)<{
  inView: boolean
  animated?: boolean
}>`
  @keyframes write-in {
    from {
      clip-path: inset(-0.5em calc(100% + 1.5em) -0.5em -1.5em);
    }
    to {
      clip-path: inset(-0.5em -1.5em -0.5em -1.5em);
    }
  }

  & > span {
    animation: write-in 0.1s linear both;
    animation-play-state: ${({ animated = true, inView }) => (inView && animated ? 'running' : 'paused')};
    animation-delay: calc(0.1s * var(--word-index, 0));
  }
`
